import React, { useId, useRef, useState } from 'react';

import { SIGN_IN_FAILED_MESSAGE } from '../../services/cognitoAuthService';
import { FILLS, SIZING, TYPE } from '../../styles/tokens';
import { SheetFooter } from '../common/Sheet';

export const LOG_IN_TOTP_STEP_DIFFERENT_ACCOUNT_LABEL = 'Use a different account';

const CODE_LENGTH = 6;

export interface LogInTotpStepProps {
  /** Submits the six digits; a rejection keeps the step open and its message is shown inline. */
  onVerify: (code: string) => Promise<void>;
  onUseDifferentAccount: () => void;
  /** Sign-in pop-up only: the primary button renders in the sheet's pinned footer. */
  pinPrimaryToSheetFooter?: boolean;
}

/** Code step for a log-in answered with an authenticator challenge. It never sees the password. */
const LogInTotpStep: React.FC<LogInTotpStepProps> = ({
  onVerify,
  onUseDifferentAccount,
  pinPrimaryToSheetFooter = false,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const idBase = useId();
  const inputId = `${idBase}-code`;
  const errorId = `${idBase}-error`;
  const formId = `${idBase}-form`;

  const canVerify = code.length === CODE_LENGTH && !isVerifying;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canVerify) return;
    setIsVerifying(true);
    setError('');
    try {
      await onVerify(code);
    } catch (caught: unknown) {
      // Only an Error carries a message written for the guest; anything else stays generic.
      setError(caught instanceof Error && caught.message ? caught.message : SIGN_IN_FAILED_MESSAGE);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyButton = (
    <button type="submit" form={formId} className="btn-gold" disabled={!canVerify}>
      {isVerifying ? (
        <span className="spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
      ) : (
        'Verify'
      )}
    </button>
  );

  return (
    <div>
      <h2 style={{ ...TYPE.itemName, color: FILLS.textPrimary, margin: '0 0 8px' }}>
        Enter your authenticator code
      </h2>
      <p style={{ ...TYPE.secondary, color: FILLS.textLight, margin: '0 0 24px' }}>
        Open your authenticator app and enter the 6-digit code for Belhop.
      </p>
      {error && (
        <div
          className="alert alert-error"
          id={errorId}
          role="alert"
          style={{ marginBottom: '16px' }}
        >
          {error}
        </div>
      )}
      <form id={formId} onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor={inputId}>
            Authentication code
          </label>
          <input
            id={inputId}
            ref={inputRef}
            className="form-input"
            type="text"
            value={code}
            onChange={(event) =>
              setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            autoFocus
            required
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            style={error ? { borderColor: FILLS.error } : undefined}
          />
        </div>
        {!pinPrimaryToSheetFooter && <div style={{ marginTop: '24px' }}>{verifyButton}</div>}
      </form>
      {pinPrimaryToSheetFooter && <SheetFooter>{verifyButton}</SheetFooter>}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <button
          type="button"
          onClick={onUseDifferentAccount}
          style={{
            minHeight: SIZING.touchTarget,
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: FILLS.gold,
            ...TYPE.secondaryStrong,
          }}
        >
          {LOG_IN_TOTP_STEP_DIFFERENT_ACCOUNT_LABEL}
        </button>
      </div>
    </div>
  );
};

export default LogInTotpStep;
